import type { Party, PortalStatement, PosCollection } from '@erp/shared';
import Decimal from 'decimal.js';
import { CheckCircle2, CreditCard, Printer, Wallet } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/shadcn/index.js';
import { Button, Combobox, type ComboboxOption } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { appUrl } from '../../lib/basePath.js';
import { formatMoney, getCurrencyDecimals } from '../../lib/money.js';
import { normalizeAmountTyping, safeDecimal } from '../../lib/numerals.js';

type Method = 'cash' | 'card' | 'wallet' | 'other';
const METHODS: Method[] = ['cash', 'card', 'wallet', 'other'];

/**
 * A customer paying off their debt at the register — the counter's way to take
 * a customer's money, so it is in THIS shift and in a drawer that is counted.
 *
 * Pick the customer (the one on the sale by default), see what they owe in
 * each currency, choose the debt to pay and the money it comes in — dinars for
 * a dollar debt are converted at today's rate, as at the sale screen — and the
 * server books a payment that settles their oldest open invoices first. The
 * shift's close-out then shows it under "debt payments".
 *
 * A MINUS amount goes the other way (owner, 2026-11-01): money handed to the
 * customer out of the drawer and added to what they owe — the customer page's
 * negative payment, at the counter. In the money's own currency, no rate.
 */
export function CollectDebtDialog({
  open,
  onOpenChange,
  sessionId,
  baseCurrency,
  parties,
  defaultPartyId,
  locale,
  onCollected,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  baseCurrency: string;
  parties: Party[] | null;
  defaultPartyId: string | null;
  locale: string;
  onCollected: (c: PosCollection) => void;
}): JSX.Element {
  const { t } = useTranslation('pos');
  const canPrint = usePermission('payments.read');
  const [partyId, setPartyId] = useState<string | null>(null);
  const [statement, setStatement] = useState<PortalStatement | null>(null);
  const [debtCcy, setDebtCcy] = useState('');
  const [payCcy, setPayCcy] = useState('');
  const [method, setMethod] = useState<Method>('cash');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  /** Multipliers taking ONE unit of the debt's currency to each other currency (today). */
  const [rates, setRates] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<PosCollection | null>(null);

  // A fresh open: the sale's customer, nothing typed.
  useEffect(() => {
    if (!open) return;
    setPartyId(defaultPartyId);
    setMethod('cash');
    setAmount('');
    setReference('');
    setNote('');
    setError(null);
    setDone(null);
  }, [open, defaultPartyId]);

  // What they owe — the same statement the customer page reads.
  useEffect(() => {
    if (!open || !partyId) { setStatement(null); return undefined; }
    let live = true;
    setStatement(null);
    void api().get<PortalStatement>(`/parties/${partyId}/statement`)
      .then((r) => {
        if (!live) return;
        setStatement(r.data);
        const owing = r.data.summary.filter((s) => Number(s.balance) > 0.0001);
        const first = owing[0]?.currencyCode ?? baseCurrency;
        setDebtCcy(first);
        setPayCcy(first);
      })
      .catch(() => { if (live) setStatement(null); });
    return () => { live = false; };
  }, [open, partyId, baseCurrency, done]);

  useEffect(() => {
    if (!open || !debtCcy) return undefined;
    let live = true;
    void api().get<{ items: Array<{ code: string; rate: string }> }>('/currencies/display-rates', { params: { base: debtCcy } })
      .then((r) => { if (live) setRates(Object.fromEntries(r.data.items.map((x) => [x.code, x.rate]))); })
      .catch(() => { if (live) setRates({}); });
    return () => { live = false; };
  }, [open, debtCcy]);

  const customers: ComboboxOption[] = useMemo(() => (parties ?? [])
    .filter((p) => p.code !== 'WALK-IN' && p.partyType !== 'vendor' && p.isActive !== false)
    .map((p) => ({
      value: p.id,
      label: p.displayName,
      sublabel: [p.code, p.phone].filter(Boolean).join(' · '),
      keywords: [p.code, p.phone, p.displayName].filter(Boolean).join(' '),
    })), [parties]);

  const money = (v: string | Decimal, ccy: string): string => formatMoney(v.toString(), { locale, currency: ccy });
  const owing = (statement?.summary ?? []).filter((s) => Number(s.balance) > 0.0001);
  const debtOptions = owing.length ? owing.map((s) => s.currencyCode) : [debtCcy || baseCurrency];
  const owed = new Decimal(owing.find((s) => s.currencyCode === debtCcy)?.balance ?? '0');
  // The money it comes in: the debt's own, or another one the shop converts today.
  const payOptions = [debtCcy, ...Object.keys(rates).filter((c) => c !== debtCcy).sort()].filter(Boolean);
  const pay = payOptions.includes(payCcy) ? payCcy : debtCcy;
  const cross = !!pay && pay !== debtCcy;
  const rate = cross ? new Decimal(rates[pay] ?? '0') : new Decimal(1);
  const payDp = getCurrencyDecimals(pay) ?? 2;
  const typed = safeDecimal(amount);
  /** A minus: money handed to the customer, added to what they owe in `pay`. */
  const givenOut = typed.lt(0);
  const owedInPay = new Decimal(owing.find((s) => s.currencyCode === pay)?.balance ?? '0');
  /** What the typed money pays, in the debt's currency. */
  const pays = cross ? (rate.gt(0) ? typed.dividedBy(rate) : new Decimal(0)) : typed;
  /** The whole debt, in the money being paid with. */
  const fullInPay = cross ? owed.times(rate).toDecimalPlaces(payDp, Decimal.ROUND_UP) : owed;
  const after = Decimal.max(0, owed.minus(pays));
  const canSubmit = !busy && !!partyId && !typed.isZero() && (givenOut || !cross || rate.gt(0));
  const rateText = cross && rate.gt(0)
    ? (rate.gte(1) ? `1 ${debtCcy} = ${money(rate, pay)}` : `1 ${pay} = ${money(new Decimal(1).dividedBy(rate).toDecimalPlaces(2), debtCcy)}`)
    : '';

  const submit = async (): Promise<void> => {
    if (!canSubmit || !partyId) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api().post<PosCollection>(`/pos/sessions/${sessionId}/collections`, {
        partyId,
        amount: typed.toFixed(payDp),
        currencyCode: pay,
        // Money given out is owed back in the same money — no conversion.
        debtCurrencyCode: givenOut ? pay : debtCcy,
        method,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setDone(r.data);
      onCollected(r.data);
    } catch (e) {
      const code = (e as { response?: { data?: { cause?: { code?: string } } } }).response?.data?.cause?.code;
      setError(
        code === 'session_not_open' ? t('collect.err.closed', { defaultValue: 'This shift is closed — open a shift to take money.' })
          : code === 'session_not_yours' ? t('collect.err.notYours', { defaultValue: 'This shift belongs to another cashier.' })
            : code === 'collect_party_invalid' ? t('collect.err.party', { defaultValue: 'Pick a customer with an account.' })
              : t('collect.err.failed', { defaultValue: 'The payment could not be taken. Try again.' }),
      );
    } finally {
      setBusy(false);
    }
  };

  const partyName = parties?.find((p) => p.id === partyId)?.displayName ?? '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl gap-3 p-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-fg-muted" aria-hidden />
            {t('collect.title', { defaultValue: 'Customer pays their debt' })}
          </DialogTitle>
        </DialogHeader>

        {done ? (
          <div className="flex flex-col items-center gap-3 py-3 text-center">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-success/15 text-success">
              <CheckCircle2 className="h-7 w-7" aria-hidden />
            </span>
            <div className="text-lg font-bold">
              {Number(done.notesAmount) < 0
                ? t('collect.doneGiven', {
                  defaultValue: '{{amount}} given to {{name}} — added to their account',
                  amount: money(new Decimal(done.notesAmount).abs(), done.notesCurrency),
                  name: done.partyName ?? partyName,
                })
                : t('collect.done', {
                  defaultValue: '{{amount}} taken from {{name}}',
                  amount: money(done.notesAmount, done.notesCurrency),
                  name: done.partyName ?? partyName,
                })}
            </div>
            {done.notesCurrency !== done.currencyCode && (
              <div className="text-sm text-fg-muted">
                {t('collect.paysDebt', { defaultValue: 'pays {{amount}} of the debt', amount: money(done.amount, done.currencyCode) })}
              </div>
            )}
            <div className="font-mono text-xs text-fg-muted">{done.paymentNumber}</div>
            {statement && (
              <div className="text-sm">
                {t('collect.stillOwes', { defaultValue: 'Still owes' })}:{' '}
                <span className="font-semibold">
                  {owing.length
                    ? owing.map((s) => money(s.balance, s.currencyCode)).join(' · ')
                    : t('collect.nothing', { defaultValue: 'nothing — settled' })}
                </span>
              </div>
            )}
            <div className="flex flex-wrap justify-center gap-2">
              {canPrint && (
                <Button variant="secondary" onClick={() => window.open(appUrl(`/payments/${done.paymentId}/print`), '_blank')}>
                  <Printer className="h-4 w-4" aria-hidden />
                  {t('collect.print', { defaultValue: 'Print receipt' })}
                </Button>
              )}
              <Button onClick={() => onOpenChange(false)}>{t('collect.close', { defaultValue: 'Done' })}</Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <Combobox
              options={customers}
              value={partyId ?? undefined}
              onChange={(v) => setPartyId(v ?? null)}
              placeholder={t('collect.customer', { defaultValue: 'Customer…' })}
              ariaLabel={t('collect.customer', { defaultValue: 'Customer…' })}
              autoFocus={!defaultPartyId}
            />

            {partyId && (
              <div className="rounded-lg border border-border bg-bg-subtle/40 px-3 py-2 text-sm">
                <span className="text-fg-muted">{t('collect.owes', { defaultValue: 'Owes' })}: </span>
                {statement === null ? '…' : owing.length
                  ? <span className="font-semibold text-warning">{owing.map((s) => money(s.balance, s.currencyCode)).join(' · ')}</span>
                  : <span className="text-success">{t('collect.nothing', { defaultValue: 'nothing — settled' })}</span>}
              </div>
            )}

            {partyId && debtOptions.length > 1 && (
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-fg-muted">{t('collect.whichDebt', { defaultValue: 'Pays the debt in' })}</span>
                <div className="grid gap-1 rounded-lg border border-border bg-bg p-1" style={{ gridTemplateColumns: `repeat(${debtOptions.length}, 1fr)` }}>
                  {debtOptions.map((c) => (
                    <button key={c} type="button" onClick={() => { setDebtCcy(c); setPayCcy(c); setAmount(''); }} aria-pressed={debtCcy === c}
                      className={`rounded-md py-1.5 text-sm font-bold transition ${debtCcy === c ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:bg-bg-subtle'}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-4 gap-1.5">
              {METHODS.map((m) => (
                <button key={m} type="button" onClick={() => setMethod(m)} aria-pressed={method === m}
                  className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-sm font-semibold transition ${method === m ? 'border-brand bg-brand-subtle text-fg' : 'border-border bg-bg text-fg-muted hover:bg-bg-subtle'}`}>
                  {m === 'cash' ? <Wallet className="h-4 w-4" aria-hidden /> : <CreditCard className="h-4 w-4" aria-hidden />}
                  {t(`tender.${m}`)}
                </button>
              ))}
            </div>

            {payOptions.length > 1 && (
              <div className="grid gap-1 rounded-lg border border-border bg-bg p-1" style={{ gridTemplateColumns: `repeat(${payOptions.length}, 1fr)` }}>
                {payOptions.map((c) => (
                  <button key={c} type="button" onClick={() => { setPayCcy(c); setAmount(''); }} aria-pressed={pay === c}
                    className={`rounded-md py-1.5 text-sm font-bold transition ${pay === c ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:bg-bg-subtle'}`}>
                    {c}
                  </button>
                ))}
              </div>
            )}

            <label className="flex flex-col gap-1 text-xs font-medium text-fg-muted">
              {t('collect.amountIn', { defaultValue: 'Amount received in {{ccy}}', ccy: pay })}
              <input
                value={amount}
                onChange={(e) => setAmount(normalizeAmountTyping(e.target.value))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submit(); } }}
                inputMode="decimal"
                dir="ltr"
                className={`h-11 rounded-md border bg-bg px-3 text-end font-mono text-xl outline-none ${givenOut ? 'border-danger text-danger focus:border-danger' : 'border-border text-fg focus:border-brand'}`}
                placeholder={owed.gt(0) ? fullInPay.toFixed(payDp) : '0'}
              />
            </label>
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-fg-muted">
              {owed.gt(0) ? (
                <button type="button" className="underline hover:text-fg" onClick={() => setAmount(fullInPay.toFixed(payDp))}>
                  {t('collect.all', { defaultValue: 'The whole debt: {{amount}}', amount: money(fullInPay, pay) })}
                </button>
              ) : <span />}
              {cross && <span dir="ltr">{rateText}</span>}
            </div>
            {/* A minus: say plainly that the money leaves the drawer and what they owe after. */}
            {givenOut && (
              <div className="flex flex-col gap-1 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm">
                <span className="font-semibold text-danger">
                  {t('collect.givenHint', { defaultValue: 'Minus: money handed to the customer from this drawer — added to what they owe.' })}
                </span>
                <span className="flex items-center justify-between">
                  <span className="text-fg-muted">{t('collect.givenAfter', { defaultValue: 'Owes after this' })}</span>
                  <span className="font-mono font-semibold tabular-nums" dir="ltr">{money(owedInPay.plus(typed.abs()), pay)}</span>
                </span>
              </div>
            )}
            {typed.gt(0) && (
              <div className="flex items-center justify-between rounded-lg bg-bg-subtle/60 px-3 py-2 text-sm">
                <span className="text-fg-muted">
                  {cross
                    ? t('collect.paysDebt', { defaultValue: 'pays {{amount}} of the debt', amount: money(pays.toDecimalPlaces(getCurrencyDecimals(debtCcy) ?? 2), debtCcy) })
                    : t('collect.leaves', { defaultValue: 'Left to pay after this' })}
                </span>
                <span className="font-mono font-semibold tabular-nums" dir="ltr">
                  {cross ? '' : money(after, debtCcy)}
                </span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder={t('tender.reference', { defaultValue: 'Reference' })}
                className="h-9 rounded-md border border-border bg-bg px-2.5 text-sm text-fg outline-none focus:border-brand" />
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('collect.note', { defaultValue: 'Note (optional)' })}
                className="h-9 rounded-md border border-border bg-bg px-2.5 text-sm text-fg outline-none focus:border-brand" />
            </div>

            {error && <div className="rounded-md bg-danger/10 px-3 py-2 text-sm font-semibold text-danger">{error}</div>}

            <Button onClick={() => void submit()} disabled={!canSubmit} loading={busy} variant={givenOut ? 'danger' : 'primary'}>
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              {givenOut
                ? t('collect.giveSubmit', { defaultValue: 'Give the money' })
                : t('collect.submit', { defaultValue: 'Take the payment' })}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
